import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { companyService } from '@/services/company.service'
import { companySettingsService } from '@/services/company_settings.service'
import { invoiceService } from '@/services/invoice.service'
import { useCompany } from '@/hooks/useCompany'
import { maskCnpj, isValidCnpj } from '@/lib/validators'
import type { Company } from '@/types/company.types'
import {
  IconCheck,
  IconBuilding,
  IconFileText,
  IconDollarSign,
  IconClipboardList,
} from '@/components/Icons'
import { Page } from '@/components/ui/Page'
import { Card } from '@/components/ui/Card'
import { FormRow, FormField } from '@/components/ui/Form'
import { Button } from '@/components/ui/Button'
import { CompanySubscriptionManager } from '@/components/CompanySubscriptionManager'
import { TimelineAuditTrail } from '@/components/ui/TimelineAuditTrail'

export type CompanyEditTab = 'data' | 'fiscal' | 'subscription' | 'audit'

const VALID_TABS: CompanyEditTab[] = ['data', 'fiscal', 'subscription', 'audit']

interface FormErrors { name?: string; cnpj?: string }

function validateCompany(name: string, cnpj: string, isPersonal = false): FormErrors {
  const e: FormErrors = {}
  if (!name.trim() || name.trim().length < 2) e.name = 'Nome deve ter ao menos 2 caracteres'
  if (!isPersonal) {
    if (!cnpj) e.cnpj = 'CNPJ é obrigatório'
    else if (!isValidCnpj(cnpj)) e.cnpj = 'CNPJ inválido'
  } else if (cnpj && !isValidCnpj(cnpj)) {
    e.cnpj = 'CNPJ inválido'
  }
  return e
}

interface FiscalErrors {
  fiscalName?: string
  munInscr?: string
  zip?: string
  street?: string
  number?: string
  neighborhood?: string
  city?: string
  uf?: string
  email?: string
  serviceItem?: string
}

function validateFiscal(
  fiscalName: string,
  munInscr: string,
  zip: string,
  street: string,
  number: string,
  neighborhood: string,
  city: string,
  uf: string,
  email: string,
  serviceItem: string
): FiscalErrors {
  const e: FiscalErrors = {}
  if (!fiscalName.trim()) e.fiscalName = 'Razão Social é obrigatória'
  if (!munInscr.trim()) e.munInscr = 'Inscrição Municipal é obrigatória'
  if (!zip.trim()) e.zip = 'CEP é obrigatório'
  if (!street.trim()) e.street = 'Logradouro é obrigatório'
  if (!number.trim()) e.number = 'Número é obrigatório'
  if (!neighborhood.trim()) e.neighborhood = 'Bairro é obrigatório'
  if (!city.trim()) e.city = 'Município é obrigatório'
  if (!uf.trim()) e.uf = 'UF é obrigatória'
  if (!email.trim()) e.email = 'E-mail é obrigatório'
  if (!serviceItem.trim()) e.serviceItem = 'Código de Item LC116 é obrigatório'
  return e
}

const fiscalFieldLabels: Record<keyof FiscalErrors, string> = {
  fiscalName: 'Razão Social',
  munInscr: 'Inscrição Municipal',
  zip: 'CEP',
  street: 'Logradouro',
  number: 'Número',
  neighborhood: 'Bairro',
  city: 'Município',
  uf: 'UF',
  email: 'E-mail',
  serviceItem: 'Código de Item LC116'
}


function applyPhoneMask(val: string) {
  let v = val.replace(/\D/g, '')
  if (v.length > 11) v = v.substring(0, 11)
  if (v.length > 10) return v.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
  if (v.length > 6)  return v.replace(/^(\d{2})(\d{4})(\d)/, '($1) $2-$3')
  if (v.length > 2)  return v.replace(/^(\d{2})(\d)/, '($1) $2')
  if (v.length > 0)  return v.replace(/^(\d+)/, '($1')
  return v
}

function formatZip(val: string) {
  let v = val.replace(/\D/g, '')
  if (v.length > 8) v = v.substring(0, 8)
  return v.replace(/^(\d{5})(\d)/, '$1-$2')
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function CompanyFormPage() {
  const { companyId: paramCompanyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshCompanies, isMasterCompany, isTitularTenant, activeCompany, companies, loadingCompanies } = useCompany()

  const isSelfServiceEdit = location.pathname.endsWith('/company/edit')
  const companyId = paramCompanyId || (isSelfServiceEdit ? activeCompany?.id : undefined)
  const isEdit = Boolean(paramCompanyId) || isSelfServiceEdit
  const canNavigateToCompaniesList = !isSelfServiceEdit && Boolean(isTitularTenant ?? isMasterCompany)
  const backPath = canNavigateToCompaniesList ? '/dashboard/companies' : '/dashboard'
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as CompanyEditTab | null
  const currentTab: CompanyEditTab = isEdit && tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'data'

  const handleTabChange = (newTab: CompanyEditTab) => {
    setSearchParams({ tab: newTab })
  }

  // Company state
  const initialCompany =
    (location.state as { company?: Company } | null)?.company ??
    (companyId && activeCompany && activeCompany.id === companyId && (activeCompany.cnpj !== undefined || activeCompany.account_type === 'personal')
      ? activeCompany
      : null)
  const [company, setCompany] = useState<Company | null>(initialCompany)
  const [loadingCompany, setLoadingCompany] = useState(isEdit && !initialCompany)
  const isPersonalAccount = company?.account_type === 'personal'

  // Company form
  const [name, setName] = useState(initialCompany?.name ?? '')
  const [cnpj, setCnpj] = useState(initialCompany ? maskCnpj(initialCompany.cnpj ?? '') : '')
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState({ name: false, cnpj: false })
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Company settings (edit mode only)
  const [useBillingDate,     setUseBillingDate]     = useState(false)
  const [savingSettings,     setSavingSettings]     = useState(false)
  const [settingsLoaded,     setSettingsLoaded]     = useState(false)
  const [varianceAlertDropdown, setVarianceAlertDropdown] = useState<string>('120')
  const [varianceAlertCustomValue, setVarianceAlertCustomValue] = useState<string>('120')
  const [varianceAlertError, setVarianceAlertError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  function validateVariancePercent(dropdown: string, custom: string) {
    if (dropdown !== 'custom') return null
    if (!custom || !custom.trim()) {
      return 'O percentual limite deve ser um número inteiro maior que zero.'
    }
    const num = Number(custom)
    if (isNaN(num) || num <= 0 || !Number.isInteger(num)) {
      return 'O percentual limite deve ser um número inteiro maior que zero.'
    }
    if (num < 1 || num > 1000) {
      return 'O percentual limite deve ser entre 1 e 1000.'
    }
    return null
  }

  function handleDropdownChange(val: string) {
    setVarianceAlertDropdown(val)
    if (val !== 'custom') {
      setVarianceAlertError(null)
      setVarianceAlertCustomValue(val)
    } else {
      const err = validateVariancePercent('custom', varianceAlertCustomValue)
      setVarianceAlertError(err)
    }
  }

  function handleCustomValueChange(val: string) {
    setVarianceAlertCustomValue(val)
    const err = validateVariancePercent('custom', val)
    setVarianceAlertError(err)
  }

  // Fiscal settings states
  const [fiscalEnabled, setFiscalEnabled] = useState(false)
  const [loadingFiscal, setLoadingFiscal] = useState(isEdit)
  const [fiscalError, setFiscalError] = useState<string | null>(null)
  const [fiscalSuccess, setFiscalSuccess] = useState<string | null>(null)
  const [fiscalErrors, setFiscalErrors] = useState<FiscalErrors>({})
  const [showFiscalErrors, setShowFiscalErrors] = useState(false)

  const [env, setEnv] = useState<'sandbox' | 'production'>('sandbox')
  const [fiscalName, setFiscalName] = useState('')
  const [fantasyName, setFantasyName] = useState('')
  const [munInscr, setMunInscr] = useState('')
  const [stateInscr, setStateInscr] = useState('')
  const [regime, setRegime] = useState(1)

  const [street, setStreet] = useState('')
  const [number, setNumber] = useState('')
  const [complement, setComplement] = useState('')
  const [neighborhood, setNeighborhood] = useState('')
  const [city, setCity] = useState('')
  const [uf, setUf] = useState('')
  const [zip, setZip] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const [serviceItem, setServiceItem] = useState('')
  const [munService, setMunService] = useState('')
  const [taxPercentage, setTaxPercentage] = useState(0)
  const [issRetained, setIssRetained] = useState(false)

  // Novos campos RPS e CNAE (PRD: feature-fiscal-settings-nfse-campos-rps-cnae)
  const [cnaeCode, setCnaeCode] = useState('')
  const [codigoTributacaoMunicipio, setCodigoTributacaoMunicipio] = useState('')
  const [rpsSerie, setRpsSerie] = useState('RPS')
  const [rpsTipo, setRpsTipo] = useState(1)
  const [rpsLastNumber, setRpsLastNumber] = useState(0)

  const [certExpires, setCertExpires] = useState<string | null>(null)
  const [hasToken, setHasToken] = useState(false)
  const [hasTokenSandbox, setHasTokenSandbox] = useState(false)

  const [certFile, setCertFile] = useState<File | null>(null)
  const [certPassword, setCertPassword] = useState('')

  const [searchingCep, setSearchingCep] = useState(false)
  const [searchingCnpj, setSearchingCnpj] = useState(false)

  // Load data in edit mode
  useEffect(() => {
    if (!isEdit) return
    if (loadingCompanies) return
    if (!companyId) {
      navigate(backPath, { replace: true })
      return
    }

    async function load() {
      if (!company || company.id !== companyId) {
        const contextCompany =
          (activeCompany && activeCompany.id === companyId && (activeCompany.cnpj !== undefined || activeCompany.account_type === 'personal')
            ? activeCompany
            : undefined) ??
          companies?.find(c => c.id === companyId)

        if (contextCompany) {
          setCompany(contextCompany)
          setName(contextCompany.name)
          setCnpj(maskCnpj(contextCompany.cnpj ?? ''))
          setLoadingCompany(false)
        } else {
          setLoadingCompany(true)
          try {
            const list = await companyService.list()
            const found = list.find(c => c.id === companyId)
            if (!found) { navigate(backPath, { replace: true }); return }
            setCompany(found)
            setName(found.name)
            setCnpj(maskCnpj(found.cnpj ?? ''))
          } catch (err) {
            console.error(err)
          } finally {
            setLoadingCompany(false)
          }
        }
      } else {
        setName(company.name)
        setCnpj(maskCnpj(company.cnpj ?? ''))
      }

      setLoadingFiscal(true)
      try {
        const data = await invoiceService.getFiscalSettings(companyId!)
        if (data && data.company_cnpj && data.company_name) {
          setFiscalEnabled(true)
          setEnv(data.focus_environment || 'sandbox')
          setFiscalName(data.company_name || '')
          setFantasyName(data.company_fantasy_name || '')
          setMunInscr(data.municipal_inscription || '')
          setStateInscr(data.state_inscription || '')
          setRegime(data.regime_tributario || 1)
          setStreet(data.address_street || '')
          setNumber(data.address_number || '')
          setComplement(data.address_complement || '')
          setNeighborhood(data.address_neighborhood || '')
          setCity(data.address_city || '')
          setUf(data.address_uf || '')
          setZip(data.address_zip || '')
          setEmail(data.company_email || '')
          setPhone(data.company_phone || '')
          setServiceItem(data.service_item_code || '')
          setMunService(data.municipal_service_code || '')
          setTaxPercentage(data.tax_percentage || 0)
          setIssRetained(data.iss_retained || false)
          // Novos campos RPS e CNAE
          setCnaeCode(data.cnae_code || '')
          setCodigoTributacaoMunicipio(data.codigo_tributacao_municipio || '')
          setRpsSerie(data.rps_serie || 'RPS')
          setRpsTipo(data.rps_tipo || 1)
          setRpsLastNumber(data.rps_last_number || 0)
          setCertExpires(data.certificate_expires_at || null)
          setHasToken(data.has_token || false)
          setHasTokenSandbox(data.has_token_sandbox || false)
          setFiscalErrors({})
          setShowFiscalErrors(false)
        } else {
          setFiscalEnabled(false)
        }
      } catch (err: any) {
        setFiscalError('Erro ao carregar configurações fiscais.')
      } finally {
        setLoadingFiscal(false)
      }
    }

    void load()
  }, [companyId, isEdit, loadingCompanies])

  // Load company settings in edit mode
  useEffect(() => {
    if (!isEdit || !companyId) return
    companySettingsService.get(companyId)
      .then(s => {
        setUseBillingDate(s.use_billing_date)
        const percent = s.variance_alert_percent ?? 120
        const percentStr = String(percent)
        const standardOptions = ['5', '10', '20', '30', '50', '100', '120']
        if (standardOptions.includes(percentStr)) {
          setVarianceAlertDropdown(percentStr)
        } else {
          setVarianceAlertDropdown('custom')
        }
        setVarianceAlertCustomValue(percentStr)
        setSettingsLoaded(true)
      })
      .catch(() => setSettingsLoaded(true))
  }, [companyId])

  // Reset saved status after 4 seconds for a better UX
  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 4000)
      return () => clearTimeout(t)
    }
  }, [saved])

  async function handleToggleBillingDate(checked: boolean) {
    if (!companyId) return
    try {
      setSavingSettings(true)
      await companySettingsService.update(companyId, { use_billing_date: checked })
      setUseBillingDate(checked)
    } finally {
      setSavingSettings(false)
    }
  }

  function touch(field: 'name' | 'cnpj') {
    setTouched(t => ({ ...t, [field]: true }))
    setFormErrors(validateCompany(name, cnpj, isPersonalAccount))
  }

  async function handleLookupCnpj() {
    const rawCnpj = cnpj.replace(/\D/g, '')
    if (rawCnpj.length !== 14) return

    try {
      setSearchingCnpj(true)
      setFiscalEnabled(true)
      setFiscalError(null)
      setFiscalSuccess(null)
      const data = await companyService.lookupCnpj(rawCnpj)
      
      const nextFiscalName = data.razao_social || ''
      const nextZip = data.cep ? formatZip(data.cep) : ''
      const nextStreet = data.logradouro || ''
      const nextNumber = data.numero || ''
      const nextNeighborhood = data.bairro || ''
      const nextCity = data.municipio || ''
      const nextUf = data.uf || ''
      const nextEmail = data.email || ''
      const nextPhone = data.ddd_telefone_1 
        ? applyPhoneMask(data.ddd_telefone_1) 
        : (data.ddd && data.telefone ? applyPhoneMask(data.ddd + data.telefone) : '')

      if (data.razao_social) {
        setFiscalName(data.razao_social)
        setName(data.razao_social)
      }
      if (data.nome_fantasia) setFantasyName(data.nome_fantasia)
      if (data.cep) setZip(formatZip(data.cep))
      if (data.logradouro) setStreet(data.logradouro)
      if (data.numero) setNumber(data.numero)
      if (data.complemento) setComplement(data.complemento)
      if (data.bairro) setNeighborhood(data.bairro)
      if (data.municipio) setCity(data.municipio)
      if (data.uf) setUf(data.uf)
      if (data.email) setEmail(data.email)
      if (nextPhone) setPhone(nextPhone)

      if (data.opcao_pelo_mei === true) {
        setRegime(4)
      } else if (data.opcao_pelo_simples === true) {
        setRegime(1)
      } else if (data.opcao_pelo_simples === false) {
        setRegime(3)
      }

      if (data.cnae_fiscal) {
        // Preenche Código CNAE com o valor da Receita Federal (cnae_fiscal)
        setCnaeCode(String(data.cnae_fiscal))
      }

      if (showFiscalErrors) {
        const fErrs = validateFiscal(
          nextFiscalName || fiscalName,
          munInscr,
          nextZip || zip,
          nextStreet || street,
          nextNumber || number,
          nextNeighborhood || neighborhood,
          nextCity || city,
          nextUf || uf,
          nextEmail || email,
          serviceItem
        )
        setFiscalErrors(fErrs)
      }

      setFiscalSuccess('Dados cadastrais do CNPJ recuperados com sucesso!')
    } catch (err: any) {
      setFiscalError(err.message || 'Erro ao buscar dados do CNPJ.')
    } finally {
      setSearchingCnpj(false)
    }
  }

  async function handleCepChange(val: string) {
    const masked = formatZip(val)
    setZip(masked)
    if (showFiscalErrors) {
      setFiscalErrors(prev => ({
        ...prev,
        zip: masked.trim() ? undefined : 'CEP é obrigatório'
      }))
    }
    const cleanCep = masked.replace(/\D/g, '')
    if (cleanCep.length === 8) {
      setSearchingCep(true)
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
        const data = await res.json()
        if (!data.erro) {
          const nextStreet = data.logradouro || ''
          const nextNeighborhood = data.bairro || ''
          const nextCity = data.localidade || ''
          const nextUf = data.uf || ''

          if (data.logradouro) setStreet(data.logradouro)
          if (data.bairro) setNeighborhood(data.bairro)
          if (data.localidade) setCity(data.localidade)
          if (data.uf) setUf(data.uf)

          if (showFiscalErrors) {
            setFiscalErrors(prev => ({
              ...prev,
              street: (nextStreet || street).trim() ? undefined : 'Logradouro é obrigatório',
              neighborhood: (nextNeighborhood || neighborhood).trim() ? undefined : 'Bairro é obrigatório',
              city: (nextCity || city).trim() ? undefined : 'Município é obrigatório',
              uf: (nextUf || uf).trim() ? undefined : 'UF é obrigatória'
            }))
          }
        }
      } catch {
        // silent error
      } finally {
        setSearchingCep(false)
      }
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setTouched({ name: true, cnpj: true })
    const errs = validateCompany(name, cnpj, isPersonalAccount)
    setFormErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitError(null)
    setFiscalError(null)
    setFiscalSuccess(null)
    setSubmitting(true)

    try {
      const cnpjRaw = cnpj.replace(/\D/g, '')
      if (isEdit && company) {
        const payload: any = {
          name: name.trim(),
          cnpj: cnpjRaw,
        }

        if (fiscalEnabled) {
          const fErrs = validateFiscal(
            fiscalName,
            munInscr,
            zip,
            street,
            number,
            neighborhood,
            city,
            uf,
            email,
            serviceItem
          )
          setFiscalErrors(fErrs)
          setShowFiscalErrors(true)

          if (Object.keys(fErrs).length > 0) {
            const missingLabels = Object.keys(fErrs)
              .map(key => fiscalFieldLabels[key as keyof FiscalErrors])
              .filter(Boolean)
            setFiscalError(`Para salvar as configurações fiscais, preencha os campos obrigatórios: ${missingLabels.join(', ')}.`)
            setSubmitting(false)
            return
          }

          let base64Cert: string | undefined = undefined
          if (certFile) {
            if (!certPassword) {
              setFiscalError('A senha do certificado digital é obrigatória.')
              setSubmitting(false)
              return
            }
            base64Cert = await readFileAsBase64(certFile)
          }

          payload.fiscalSettings = {
            focus_environment: env,
            company_cnpj: cnpjRaw,
            company_name: fiscalName.trim(),
            company_fantasy_name: fantasyName.trim() || undefined,
            municipal_inscription: munInscr.trim(),
            state_inscription: stateInscr.trim() || undefined,
            regime_tributario: Number(regime),
            address_street: street.trim(),
            address_number: number.trim(),
            address_complement: complement.trim() || undefined,
            address_neighborhood: neighborhood.trim(),
            address_city: city.trim(),
            address_uf: uf.trim(),
            address_zip: zip.replace(/\D/g, ''),
            company_email: email.trim(),
            company_phone: phone.replace(/\D/g, '') || undefined,
            service_item_code: serviceItem.trim(),
            municipal_service_code: munService.trim() || undefined,
            tax_percentage: Number(taxPercentage),
            iss_retained: issRetained,
            // Novos campos RPS e CNAE
            cnae_code: cnaeCode.trim() || undefined,
            codigo_tributacao_municipio: codigoTributacaoMunicipio.trim() || undefined,
            rps_serie: rpsSerie.trim() || 'RPS',
            rps_tipo: Number(rpsTipo) as 1 | 2 | 3,
            rps_last_number: rpsLastNumber !== undefined && rpsLastNumber !== null ? Number(rpsLastNumber) : undefined,
            certificate_file: base64Cert,
            certificate_password: certPassword || undefined,
          }
        } else {
          setFiscalErrors({})
          setShowFiscalErrors(false)
        }

        const updated = await companyService.update(company.id, payload)

        const finalPercent = varianceAlertDropdown === 'custom' 
          ? Number(varianceAlertCustomValue) 
          : Number(varianceAlertDropdown)

        try {
          setSavingSettings(true)
          await companySettingsService.update(company.id, { 
            variance_alert_percent: finalPercent 
          })
        } catch (settingsErr: any) {
          setToast({
            message: 'Falha de comunicação. Não foi possível salvar as configurações de alerta orçamentário.',
            type: 'error'
          })
          setTimeout(() => setToast(null), 4000)
          setSubmitting(false)
          setSavingSettings(false)
          return
        } finally {
          setSavingSettings(false)
        }
        
        if (updated) {
          try {
            const data = await invoiceService.getFiscalSettings(company.id)
            if (data) {
              setCertExpires(data.certificate_expires_at || null)
              setHasToken(data.has_token || false)
              setHasTokenSandbox(data.has_token_sandbox || false)
            }
          } catch {
            // ignore
          }
        }

        setCertFile(null)
        setCertPassword('')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }

        if (updated && updated.id) {
          setCompany(updated)
        }
        await refreshCompanies()
        setSaved(true)
        if (canNavigateToCompaniesList) {
          navigate('/dashboard/companies', { replace: true })
        }
      } else {
        await companyService.create({ name: name.trim(), cnpj: cnpjRaw })
        await refreshCompanies()
        navigate('/dashboard/companies', { replace: true })
      }
    } catch (err) {
      const msg = (err as Error).message
      if (msg.includes('companies_owner_cnpj_idx') || msg.includes('duplicate key')) {
        setSubmitError('Já existe uma empresa cadastrada com este CNPJ.')
      } else {
        setSubmitError(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const formattedExpirationDate = certExpires
    ? new Date(certExpires).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    : null

  if (loadingCompany) {
    return (
      <Page>
        <div className="ui-empty-state">Carregando empresa...</div>
      </Page>
    )
  }

  return (
    <Page>
      <Page.Header
        title={isEdit && company ? company.name : 'Nova empresa'}
        subtitle={isEdit ? 'Editar dados da empresa' : 'Preencha os dados abaixo'}
        onBack={() => navigate(backPath)}
      />

      <Page.Content>
        {isEdit && (
          <div
            role="tablist"
            aria-label="Abas de configuração da empresa"
            style={{
              display: 'flex',
              gap: '8px',
              borderBottom: '1px solid var(--border)',
              marginBottom: '1.5rem',
              overflowX: 'auto',
            }}
          >
            <button
              type="button"
              role="tab"
              aria-selected={currentTab === 'data'}
              data-testid="tab-data"
              onClick={() => handleTabChange('data')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                fontSize: '13.5px',
                fontWeight: 600,
                border: 'none',
                borderBottom:
                  currentTab === 'data'
                    ? '2.5px solid var(--primary)'
                    : '2.5px solid transparent',
                background: 'transparent',
                color: currentTab === 'data' ? 'var(--primary)' : 'var(--text-3)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <IconBuilding size={16} />
              <span>Dados Cadastrais</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={currentTab === 'fiscal'}
              data-testid="tab-fiscal"
              onClick={() => handleTabChange('fiscal')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                fontSize: '13.5px',
                fontWeight: 600,
                border: 'none',
                borderBottom:
                  currentTab === 'fiscal'
                    ? '2.5px solid var(--primary)'
                    : '2.5px solid transparent',
                background: 'transparent',
                color: currentTab === 'fiscal' ? 'var(--primary)' : 'var(--text-3)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <IconFileText size={16} />
              <span>Emissão Fiscal</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={currentTab === 'subscription'}
              data-testid="tab-subscription"
              onClick={() => handleTabChange('subscription')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                fontSize: '13.5px',
                fontWeight: 600,
                border: 'none',
                borderBottom:
                  currentTab === 'subscription'
                    ? '2.5px solid var(--primary)'
                    : '2.5px solid transparent',
                background: 'transparent',
                color: currentTab === 'subscription' ? 'var(--primary)' : 'var(--text-3)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <IconDollarSign size={16} />
              <span>Assinatura & Limites</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={currentTab === 'audit'}
              data-testid="tab-audit"
              onClick={() => handleTabChange('audit')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                fontSize: '13.5px',
                fontWeight: 600,
                border: 'none',
                borderBottom:
                  currentTab === 'audit'
                    ? '2.5px solid var(--primary)'
                    : '2.5px solid transparent',
                background: 'transparent',
                color: currentTab === 'audit' ? 'var(--primary)' : 'var(--text-3)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <IconClipboardList size={16} />
              <span>Auditoria</span>
            </button>
          </div>
        )}

        <form onSubmit={e => { void handleSubmit(e) }} className="ui-form" noValidate>
          {/* Aba 1: Dados Cadastrais */}
          <div style={{ display: !isEdit || currentTab === 'data' ? 'block' : 'none' }} data-testid="tab-content-data">
            <Card>
              <Card.Header>
                Dados da Empresa
              </Card.Header>
              <Card.Body>
                <FormRow>
                  <FormField label={<>Nome da empresa <span style={{ color: 'var(--danger)' }}>*</span></>} htmlFor="fp-name" variant="grow">
                    <input
                      id="fp-name" type="text" value={name} autoFocus={!isEdit}
                      placeholder="Empresa Comércio Ltda."
                      onChange={e => { setName(e.target.value); if (touched.name) setFormErrors(validateCompany(e.target.value, cnpj, isPersonalAccount)) }}
                      onBlur={() => touch('name')}
                      style={{ ...(formErrors.name && touched.name ? { borderColor: 'var(--danger)' } : {}) }}
                      title={formErrors.name && touched.name ? formErrors.name : undefined}
                    />
                  </FormField>

                  <FormField label={<>CNPJ {!isPersonalAccount && <span style={{ color: 'var(--danger)' }}>*</span>}</>} htmlFor="fp-cnpj" variant="w180">
                    <input
                      id="fp-cnpj" type="text" inputMode="numeric" value={cnpj} maxLength={18}
                      placeholder="00.000.000/0000-00"
                      onChange={e => { const m = maskCnpj(e.target.value); setCnpj(m); if (touched.cnpj) setFormErrors(validateCompany(name, m, isPersonalAccount)) }}
                      onBlur={() => touch('cnpj')}
                      style={{ ...(formErrors.cnpj && touched.cnpj ? { borderColor: 'var(--danger)' } : {}) }}
                      title={formErrors.cnpj && touched.cnpj ? formErrors.cnpj : undefined}
                    />
                  </FormField>
                </FormRow>
              </Card.Body>
            </Card>
          </div>

          {/* Aba 3: Emissão Fiscal */}
          {isEdit && (
            <div style={{ display: currentTab === 'fiscal' ? 'block' : 'none' }} data-testid="tab-content-fiscal">
              {loadingFiscal ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
                  <div className="ui-loading-spinner" style={{ width: 30, height: 30, border: '3px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : (
                <>
                  <Card>
                    <Card.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <label className="ui-switch" title="Ativar/Desativar Dados Fiscais">
                          <input
                            type="checkbox"
                            checked={fiscalEnabled}
                            disabled={submitting}
                            aria-label="Ativar Dados Fiscais"
                            onChange={e => {
                              const checked = e.target.checked
                              setFiscalEnabled(checked)
                              if (!checked) {
                                setShowFiscalErrors(false)
                                setFiscalErrors({})
                                setFiscalError(null)
                              }
                            }}
                          />
                          <span className="ui-switch__track" />
                        </label>
                        <span style={{ fontWeight: 600 }}>Dados Fiscais (NFS-e)</span>
                        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: fiscalEnabled ? 'var(--success-main)' : 'var(--text-3)', background: 'var(--surface-2)', padding: '2px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                          {fiscalEnabled ? 'Ativado' : 'Desativado'}
                        </span>
                      </div>

                      <Button
                        type="button"
                        variant="secondary"
                        disabled={cnpj.replace(/\D/g, '').length !== 14 || searchingCnpj}
                        onClick={handleLookupCnpj}
                        style={{ padding: '4px 10px', fontSize: '11px', height: '24px', lineHeight: 1 }}
                      >
                        {searchingCnpj ? 'Buscando...' : 'Preencher via CNPJ'}
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {!fiscalEnabled ? (
                        <div style={{ padding: '0.5rem 0', color: 'var(--text-3)', fontSize: '0.875rem' }}>
                          Módulo fiscal e emissão de NFS-e desativados para esta empresa. Ative a opção acima para habilitar o preenchimento e a validação dos dados fiscais.
                        </div>
                      ) : (
                        <>
                      {fiscalSuccess && (
                        <div style={{ fontSize: '13.5px', color: 'var(--success-main)', background: 'var(--surface-2)', border: '1px solid var(--success-main)', borderRadius: 'var(--radius-md)', padding: '.75rem 1rem', marginBottom: '1.25rem' }}>
                          {fiscalSuccess}
                        </div>
                      )}

                      <FormRow>
                        <FormField label="Ambiente de Emissão" variant="grow">
                          <select disabled={submitting} value={env} onChange={e => setEnv(e.target.value as 'sandbox' | 'production')}>
                            <option value="sandbox">Homologação / Testes (Sandbox)</option>
                            <option value="production">Produção (Real)</option>
                          </select>
                        </FormField>

                        <FormField label="Regime Tributário" variant="grow">
                          <select disabled={submitting} value={regime} onChange={e => setRegime(Number(e.target.value))}>
                            <option value={1}>Simples Nacional</option>
                            <option value={2}>Simples Nacional - excesso de sublimite</option>
                            <option value={3}>Regime Normal (Lucro Presumido/Real)</option>
                            <option value={4}>MEI (Microempreendedor Individual)</option>
                          </select>
                        </FormField>
                      </FormRow>

                      <FormRow>
                        <FormField label={<>Razão Social <span style={{ color: 'var(--danger)' }}>*</span></>} variant="grow">
                          <input
                            type="text"
                            placeholder="Nome Comercial Completo"
                            value={fiscalName}
                            disabled={submitting}
                            onChange={e => {
                              setFiscalName(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  fiscalName: e.target.value.trim() ? undefined : 'Razão Social é obrigatória'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.fiscalName && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.fiscalName && showFiscalErrors ? fiscalErrors.fiscalName : undefined}
                          />
                        </FormField>

                        <FormField label="Nome Fantasia" variant="grow">
                          <input
                            type="text"
                            placeholder="Nome Fantasia (Opcional)"
                            value={fantasyName}
                            disabled={submitting}
                            onChange={e => setFantasyName(e.target.value)}
                          />
                        </FormField>
                      </FormRow>

                      <FormRow>
                        <FormField label={<>Inscrição Municipal <span style={{ color: 'var(--danger)' }}>*</span></>} variant="grow">
                          <input
                            type="text"
                            placeholder="Inscrição Municipal"
                            value={munInscr}
                            disabled={submitting}
                            onChange={e => {
                              setMunInscr(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  munInscr: e.target.value.trim() ? undefined : 'Inscrição Municipal é obrigatória'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.munInscr && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.munInscr && showFiscalErrors ? fiscalErrors.munInscr : undefined}
                          />
                        </FormField>

                        <FormField label="Inscrição Estadual" variant="grow">
                          <input
                            type="text"
                            placeholder="Inscrição Estadual (se houver)"
                            value={stateInscr}
                            disabled={submitting}
                            onChange={e => setStateInscr(e.target.value)}
                          />
                        </FormField>
                      </FormRow>

                      <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-2)', marginTop: '1.5rem', marginBottom: '0.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.35rem' }}>Endereço Fiscal</h3>

                      <FormRow>
                        <FormField label={<>CEP <span style={{ color: 'var(--danger)' }}>*</span>{searchingCep && <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}> (buscando…)</span>}</>} variant="w140">
                          <input
                            type="text"
                            placeholder="00000-000"
                            value={zip}
                            disabled={submitting}
                            onChange={e => { void handleCepChange(e.target.value) }}
                            style={{ ...(fiscalErrors.zip && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.zip && showFiscalErrors ? fiscalErrors.zip : undefined}
                          />
                        </FormField>

                        <FormField label={<>Logradouro <span style={{ color: 'var(--danger)' }}>*</span></>} variant="grow">
                          <input
                            type="text"
                            placeholder="Rua, Avenida, etc."
                            value={street}
                            disabled={submitting}
                            onChange={e => {
                              setStreet(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  street: e.target.value.trim() ? undefined : 'Logradouro é obrigatório'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.street && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.street && showFiscalErrors ? fiscalErrors.street : undefined}
                          />
                        </FormField>

                        <FormField label={<>Número <span style={{ color: 'var(--danger)' }}>*</span></>} variant="w100">
                          <input
                            type="text"
                            placeholder="Nº"
                            value={number}
                            disabled={submitting}
                            onChange={e => {
                              setNumber(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  number: e.target.value.trim() ? undefined : 'Número é obrigatório'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.number && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.number && showFiscalErrors ? fiscalErrors.number : undefined}
                          />
                        </FormField>
                      </FormRow>

                      <FormRow>
                        <FormField label="Complemento">
                          <input
                            type="text"
                            placeholder="Sala, Andar, etc."
                            value={complement}
                            disabled={submitting}
                            onChange={e => setComplement(e.target.value)}
                          />
                        </FormField>

                        <FormField label={<>Bairro <span style={{ color: 'var(--danger)' }}>*</span></>}>
                          <input
                            type="text"
                            placeholder="Bairro"
                            value={neighborhood}
                            disabled={submitting}
                            onChange={e => {
                              setNeighborhood(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  neighborhood: e.target.value.trim() ? undefined : 'Bairro é obrigatório'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.neighborhood && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.neighborhood && showFiscalErrors ? fiscalErrors.neighborhood : undefined}
                          />
                        </FormField>

                        <FormField label={<>Município <span style={{ color: 'var(--danger)' }}>*</span></>}>
                          <input
                            type="text"
                            placeholder="Cidade"
                            value={city}
                            disabled={submitting}
                            onChange={e => {
                              setCity(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  city: e.target.value.trim() ? undefined : 'Município é obrigatório'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.city && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.city && showFiscalErrors ? fiscalErrors.city : undefined}
                          />
                        </FormField>

                        <FormField label={<>UF <span style={{ color: 'var(--danger)' }}>*</span></>} variant="w100">
                          <input
                            type="text"
                            placeholder="UF"
                            maxLength={2}
                            value={uf}
                            disabled={submitting}
                            onChange={e => {
                              setUf(e.target.value.toUpperCase())
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  uf: e.target.value.trim() ? undefined : 'UF é obrigatória'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.uf && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.uf && showFiscalErrors ? fiscalErrors.uf : undefined}
                          />
                        </FormField>
                      </FormRow>

                      <FormRow>
                        <FormField label={<>E-mail da Empresa <span style={{ color: 'var(--danger)' }}>*</span></>} variant="grow">
                          <input
                            type="email"
                            placeholder="email@empresa.com"
                            value={email}
                            disabled={submitting}
                            onChange={e => {
                              setEmail(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  email: e.target.value.trim() ? undefined : 'E-mail é obrigatório'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.email && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.email && showFiscalErrors ? fiscalErrors.email : undefined}
                          />
                        </FormField>

                        <FormField label="Telefone de Contato">
                          <input
                            type="text"
                            placeholder="(00) 00000-0000"
                            value={phone}
                            disabled={submitting}
                            onChange={e => setPhone(applyPhoneMask(e.target.value))}
                          />
                        </FormField>
                      </FormRow>

                      <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-2)', marginTop: '1.5rem', marginBottom: '0.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.35rem' }}>Serviços & Impostos</h3>

                      <FormRow>
                        <FormField label={<>Código de Item LC116 (ex: 1.05) <span style={{ color: 'var(--danger)' }}>*</span></>}>
                          <input
                            type="text"
                            placeholder="Código LC116"
                            value={serviceItem}
                            disabled={submitting}
                            onChange={e => {
                              setServiceItem(e.target.value)
                              if (showFiscalErrors) {
                                setFiscalErrors(prev => ({
                                  ...prev,
                                  serviceItem: e.target.value.trim() ? undefined : 'Código de Item LC116 é obrigatório'
                                }))
                              }
                            }}
                            style={{ ...(fiscalErrors.serviceItem && showFiscalErrors ? { borderColor: 'var(--danger)' } : {}) }}
                            title={fiscalErrors.serviceItem && showFiscalErrors ? fiscalErrors.serviceItem : undefined}
                          />
                        </FormField>

                        <FormField label="Código de Serviço Municipal (CNAE)">
                          <input
                            type="text"
                            placeholder="Código CNAE local"
                            value={munService}
                            disabled={submitting}
                            onChange={e => setMunService(e.target.value)}
                          />
                        </FormField>

                        <FormField label="Alíquota ISS (%)">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Alíquota do Imposto"
                            value={taxPercentage}
                            disabled={submitting}
                            onChange={e => setTaxPercentage(Number(e.target.value))}
                          />
                        </FormField>

                        <FormField style={{ display: 'flex', alignItems: 'center', height: '100%', paddingTop: '18px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-2)' }}>
                            <input
                              type="checkbox"
                              checked={issRetained}
                              disabled={submitting}
                              onChange={e => setIssRetained(e.target.checked)}
                              style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
                            />
                            ISS Retido na Fonte
                          </label>
                        </FormField>
                      </FormRow>

                      <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-2)', marginTop: '1.5rem', marginBottom: '0.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.35rem' }}>Configuracao de RPS</h3>

                      <FormRow>
                        <FormField label={<>Codigo CNAE da Atividade <span style={{ color: 'var(--danger)' }}>*</span></>}>
                          <input
                            type="text"
                            id="cnae-code"
                            placeholder="9511800"
                            value={cnaeCode}
                            disabled={submitting}
                            maxLength={20}
                            onChange={e => setCnaeCode(e.target.value)}
                            title="Codigo numerico CNAE da sua atividade principal (sem pontos ou tracas)"
                          />
                        </FormField>

                        <FormField label="Codigo de Tributacao do Municipio">
                          <input
                            type="text"
                            id="codigo-tributacao-municipio"
                            placeholder="14010"
                            value={codigoTributacaoMunicipio}
                            disabled={submitting}
                            maxLength={20}
                            onChange={e => setCodigoTributacaoMunicipio(e.target.value)}
                            title="Consulte a prefeitura do seu municipio para obter este codigo"
                          />
                        </FormField>
                      </FormRow>

                      <FormRow>
                        <FormField label={<>Serie do RPS <span style={{ color: 'var(--danger)' }}>*</span></>}>
                          <input
                            type="text"
                            id="rps-serie"
                            placeholder="RPS"
                            value={rpsSerie}
                            disabled={submitting}
                            maxLength={5}
                            onChange={e => setRpsSerie(e.target.value.toUpperCase())}
                            title="Serie identificadora do RPS (max. 5 caracteres, ex: RPS)"
                          />
                        </FormField>

                        <FormField label={<>Tipo do RPS <span style={{ color: 'var(--danger)' }}>*</span></>}>
                          <select
                            id="rps-tipo"
                            value={rpsTipo}
                            disabled={submitting}
                            onChange={e => setRpsTipo(Number(e.target.value))}
                          >
                            <option value={1}>RPS (padrao)</option>
                            <option value={2}>Nota Fiscal Conjugada (Mista)</option>
                            <option value={3}>Cupom</option>
                          </select>
                        </FormField>

                        <FormField label="Proximo numero de RPS">
                          <input
                            type="number"
                            id="rps-next-number"
                            min="1"
                            value={rpsLastNumber + 1}
                            disabled={submitting}
                            onChange={e => {
                              const val = Math.max(1, Number(e.target.value))
                              setRpsLastNumber(val - 1)
                            }}
                            title="Numero sequencial gerado automaticamente pelo sistema a cada emissao"
                          />
                        </FormField>
                      </FormRow>

                      <div style={{ marginTop: '1.25rem', padding: '.75rem 1rem', borderRadius: 'var(--radius-md)', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                        <span style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-2)', marginBottom: '6px' }}>Status das Credenciais Focus NFe:</span>
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem' }}>
                          <span style={{ color: hasToken ? 'var(--success-main)' : 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            ● Token Produção: {hasToken ? 'Vincular/Ativo' : 'Pendente de ativação Focus'}
                          </span>
                          <span style={{ color: hasTokenSandbox ? 'var(--success-main)' : 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            ● Token Sandbox: {hasTokenSandbox ? 'Vincular/Ativo' : 'Pendente de ativação Focus'}
                          </span>
                        </div>
                      </div>
                      </>
                    )}
                    </Card.Body>
                  </Card>

                  {fiscalEnabled && (
                    <Card>
                      <Card.Header>
                        Certificado Digital A1 (.pfx / .p12)
                      </Card.Header>
                      <Card.Body>
                        {formattedExpirationDate ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem', padding: '.75rem 1rem', borderRadius: 'var(--radius-md)', background: 'var(--surface-2)', border: '1px solid var(--success-main)' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-1)' }}>
                              Certificado Digital Ativo. Válido até: <strong style={{ color: 'var(--success-main)' }}>{formattedExpirationDate}</strong>
                            </span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem', padding: '.75rem 1rem', borderRadius: 'var(--radius-md)', background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-3)' }}>
                              Nenhum certificado digital cadastrado ainda. O envio do certificado A1 é obrigatório para assinar as notas junto às prefeituras.
                            </span>
                          </div>
                        )}

                        <FormRow>
                          <FormField label="Arquivo do Certificado (PFX / P12)">
                            <input
                              type="file"
                              accept=".pfx,.p12"
                              ref={fileInputRef}
                              disabled={submitting}
                              onChange={e => {
                                const files = e.target.files
                                if (files && files.length > 0) {
                                  setCertFile(files[0])
                                }
                              }}
                            />
                          </FormField>

                          <FormField label="Senha do Certificado">
                            <input
                              type="password"
                              placeholder="Senha de importação"
                              value={certPassword}
                              disabled={submitting}
                              onChange={e => setCertPassword(e.target.value)}
                              required={!!certFile}
                            />
                          </FormField>
                        </FormRow>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </div>
          )}

          {isEdit && settingsLoaded && (
            <div style={{ display: currentTab === 'data' ? 'block' : 'none' }}>
              <Card>
                <Card.Header>
                  Configurações Operacionais
                </Card.Header>
                <Card.Body>
                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0' }}>
                     <div>
                       <span style={{ display: 'block', fontWeight: 600, color: 'var(--text-1)' }}>Modo de Faturamento</span>
                       <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-3)', marginTop: '0.25rem' }}>
                         Ativado: exibe o campo "Data de Faturamento" e renomeia "Data Projetada" para "Data Vencimento" nos lançamentos.
                       </span>
                     </div>
                     <label className="ui-switch" title="Modo de Faturamento">
                       <input
                         type="checkbox"
                         checked={useBillingDate}
                         disabled={savingSettings}
                         onChange={e => { void handleToggleBillingDate(e.target.checked) }}
                       />
                       <span className="ui-switch__track" />
                     </label>
                   </div>

                   <div style={{ borderTop: '1px solid var(--border)', marginTop: '1rem', paddingTop: '1rem' }}>
                     <div>
                       <span style={{ display: 'block', fontWeight: 600, color: 'var(--text-1)' }}>Limite de Desvio Orçamentário</span>
                       <span style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-3)', marginTop: '0.25rem' }}>
                         Define a tolerância de desvio projetado x realizado para exibição de alertas visuais de estouro nas despesas.
                       </span>
                     </div>
                     <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                       <select
                         className="ui-select"
                         value={varianceAlertDropdown}
                         disabled={savingSettings}
                         onChange={e => handleDropdownChange(e.target.value)}
                         style={{ width: '200px' }}
                       >
                         <option value="5">5%</option>
                         <option value="10">10%</option>
                         <option value="20">20%</option>
                         <option value="30">30%</option>
                         <option value="50">50%</option>
                         <option value="100">100%</option>
                         <option value="120">120% (Padrão)</option>
                         <option value="custom">Outro valor...</option>
                       </select>

                       {varianceAlertDropdown === 'custom' && (
                         <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                           <input
                             type="number"
                             className="ui-input"
                             value={varianceAlertCustomValue}
                             disabled={savingSettings}
                             min={1}
                             max={1000}
                             onChange={e => handleCustomValueChange(e.target.value)}
                             style={{ width: '100px' }}
                           />
                           <span style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>%</span>
                         </div>
                       )}
                     </div>
                     {varianceAlertError && (
                       <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: '0.5rem', fontWeight: 600 }} className="variance-error-msg">
                         {varianceAlertError}
                       </div>
                     )}
                   </div>
                </Card.Body>
              </Card>
            </div>
          )}

          {(!isEdit || currentTab === 'data' || currentTab === 'fiscal') && (
            <>
              {(submitError || (currentTab === 'fiscal' && fiscalError) || Object.keys(formErrors).length > 0) && (
                <div style={{
                  fontSize: '13.5px',
                  color: 'var(--danger)',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--danger)',
                  borderRadius: 'var(--radius-md)',
                  padding: '.75rem 1rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  {Object.keys(formErrors).length > 0 && (
                    <div style={{ fontWeight: 600 }}>
                      <span style={{ marginRight: '6px' }}>●</span>
                      Preencha os campos obrigatórios da empresa (Nome/CNPJ).
                    </div>
                  )}
                  {currentTab === 'fiscal' && fiscalError && (
                    <div style={{ fontWeight: 600 }}>
                      <span style={{ marginRight: '6px' }}>●</span>
                      {fiscalError}
                    </div>
                  )}
                  {submitError && (
                    <div style={{ fontWeight: 600 }}>
                      <span style={{ marginRight: '6px' }}>●</span>
                      {submitError}
                    </div>
                  )}
                </div>
              )}

              <div className="ui-form-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="ui-form-actions__left">
                  {saved && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.375rem', fontSize: '13.5px', fontWeight: 600, color: 'var(--success-main)' }}>
                      <IconCheck size={14} /> Salvo com sucesso
                    </span>
                  )}
                </div>
                <div className="ui-form-actions__right" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <Button type="submit" disabled={submitting || (isEdit && currentTab === 'data' && !!varianceAlertError)}>
                    {submitting ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar empresa'}
                  </Button>
                  <Button variant="secondary" onClick={() => navigate(backPath)}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </>
          )}
        </form>

        {/* Aba: Assinatura & Limites */}
        {isEdit && currentTab === 'subscription' && (
          <div data-testid="tab-content-subscription">
            {company ? (
              <CompanySubscriptionManager companyId={company.id} />
            ) : (
              <div className="ui-empty-state">Empresa não encontrada.</div>
            )}
          </div>
        )}

        {/* Aba 5: Auditoria */}
        {isEdit && currentTab === 'audit' && (
          <div data-testid="tab-content-audit">
            {companyId ? (
              <TimelineAuditTrail tableName="companies" entityId={companyId} />
            ) : (
              <div className="ui-empty-state">Nenhum registro de auditoria disponível.</div>
            )}
          </div>
        )}
      </Page.Content>

      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: toast.type === 'success' ? 'var(--success)' : 'var(--danger)',
          color: 'var(--white)',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
          fontWeight: 600,
          animation: 'slideUp 0.2s ease'
        }}>
          {toast.message}
        </div>
      )}
    </Page>
  )
}

